import {
  DatabaseAuto,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IcommerceUser, IdataArrayResponse,
  IdeleteOneResponse,
  IfilterProps,
  Iorder,
  IorderUpdateReturn, TorderRequest
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { CommerceClient } from '../../commerce-client';

/**
 * Type alias for order properties with specific generic types
 * @template T - Type for order (default: Iorder<number, IcommerceUser>)
 */
type TorderProps = Iorder<number, IcommerceUser>;

/**
 * Represents an order in the system.
 * This class provides methods to manage and track orders,
 * including creation, retrieval, updating, and deletion of orders.
 *
 * @extends DatabaseAuto - Provides common database operation methods
 * @template TorderProps - The type definition for order properties
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link Order} class when invoking client operations.
 *
 * @usage
 * ```typescript
 * const order = await Order.getAll();
 *
 * const order = await Order.filterAll({
 *
 * });
 *
 * const order = await Order.bulkDelete({
 *
 * });
 *
 * const order = await Order.getOne('resourceId');
 *
 * const order = await Order.remove();
 *
 * ```
 */
export class Order extends DatabaseAuto {
  /** Base API endpoint for order operations */
  static readonly baseUrl = '/order';

  /** Unique reference ID for the order */
  readonly urId: string;

  /** Current status of the order */
  readonly orderStatus: TorderProps['orderStatus'];

  /** Unique number identifying the order */
  readonly orderNumber: TorderProps['orderNumber'];

  /** Related invoice information and line items */
  readonly invoiceRelated: TorderProps['invoiceRelated'];

  /** Company associated with the order */
  readonly company: TorderProps['company'];

  /** Scheduled delivery date for the order */
  readonly deliveryDate?: Date;

  /**
   * Creates a new instance of Order.
   * @param {TorderProps} data - The order data
   * @throws {Error} If the data is invalid or missing required fields
   */
  constructor(data: TorderProps) {
    super(data);

    this.urId = data.urId;
    this.orderStatus = data.orderStatus;
    this.orderNumber = data.orderNumber;
    this.invoiceRelated = data.invoiceRelated;
    this.company = data.company;
    if (data.deliveryDate) {
      this.deliveryDate = new Date(data.deliveryDate);
    }
  }

  /**
   * Retrieves all orders with pagination support.
   * @param {number} [offset=0] - The starting index for pagination
   * @param {number} [limit=20] - The maximum number of records to return
   * @returns {Promise<{ count: number; dataArr: Order[] }>}
   * An object containing the total count and an array of orders
   * @throws {Error} If there's an error fetching the orders
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = CommerceClient.ehttp
      .makeGet<IdataArrayResponse<TorderProps>>(`${Order.baseUrl}?${query}`);
    const orders = await lastValueFrom(observer$);

    return {
      count: orders.count,
      dataArr: orders.data.map((val) => new Order(val))
    };
  }

  /**
   * Filters orders based on specified criteria.
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<{ count: number; dataArr: Order[] }>}
   * An object containing the total count and an array of filtered orders
   * @throws {Error} If there's an error applying the filter
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = CommerceClient.ehttp
      .makePost<IdataArrayResponse<TorderProps>>(`${Order.baseUrl}/filter`, filter);
    const orders = await lastValueFrom(observer$);

    return {
      count: orders.count,
      dataArr: orders.data.map((val) => new Order(val))
    };
  }

  /**
   * Retrieves a single order by its ID.
   * @param {string} resourceId - The unique identifier of the order to retrieve
   * @returns {Promise<Order>} The requested order
   * @throws {Error} If the order is not found or an error occurs during retrieval
   */
  static async getOne(resourceId: string) {
    const observer$ = CommerceClient.ehttp
      .makeGet<TorderProps>(`${Order.baseUrl}/${resourceId}`);
    const order = await lastValueFrom(observer$);

    return new Order(order);
  }

  /**
   * Creates a new order record.
   * @param {TorderRequest<true>} payload - The order data to create
   * @returns {Promise<Order>} The newly created order
   * @throws {Error} If the creation fails
   */
  static async add(payload: TorderRequest<true>) {
    const observer$ = CommerceClient.ehttp
      .makePost<TorderProps>(`${Order.baseUrl}`, payload);

    const added = await lastValueFrom(observer$);

    return new Order(added);
  }

  /**
   * Deletes multiple orders based on the provided criteria.
   * @param {IbulkDelete} payload - The criteria for identifying which orders to delete
   * @returns {Promise<IbulkDeleteSuccessResponse>} The result of the bulk delete operation
   * @throws {Error} If the bulk delete operation fails
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = CommerceClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(
        `${Order.baseUrl}/bulk-delete`,
        payload
      );

    return lastValueFrom(observer$);
  }

  /**
   * Updates the current order with new values.
   * @param {TorderRequest} payload - The data to update the order with
   * @returns {Promise<IorderUpdateReturn & { order: Order }>}
   * An object containing the update result and the updated order
   * @throws {Error} If the update operation fails
   */
  async update(payload: TorderRequest) {
    const observer$ = CommerceClient.ehttp
      .makePut<IorderUpdateReturn>(
        `${Order.baseUrl}/${this._id}`,
        payload
      );

    const res = await lastValueFrom(observer$);

    return {
      ...res,
      order: new Order(res.data.order)
    };
  }

  /**
   * Removes the current order from the system.
   * @returns {Promise<IdeleteOneResponse | null>} The result of the delete operation
   * @throws {Error} If the deletion fails or the order doesn't exist
   */
  remove() {
    const observer$ = CommerceClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${Order.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
